import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import {
  PrivateRoute,
  PublicRoute,
} from "./common-ui-components/accounts/framework/AuthRoute";
import Contexts from "./app/Contexts";
import Error404 from "./app/Error404";
import Notification from "common-ui-components/components/notification";
import { logOut, Root } from "app/root/Root";
import style from "./App.module.css";
import "semantic-ui-css/semantic.min.css";
import "./semantic-ui-fix.css";
import IdleJs from "idle-js";
import TopBanner from "app/TopBanner/TopBanner";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import handleWmsMessage from "wms/WmsMessageHandler";
import FeatureHider, { renderPermittedRoutes } from "app/root/FeatureHider";
import RestrictionChecker, {
  useNotificationAutoCleanup,
} from "common-ui-components/components/restrictionUI/RestrictionChecker";
import TryProduct from "./app/try-product/TryProduct";
import DynamicPageTitle from "DynamicPageTitle";
import { getProductsByRouteGroup, PRODUCT_CONFIG } from "ProductConfig";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";
import ModalV2 from "common-ui-components/components/modal/ModalV2";
import CustomerAddBlockPage from "app/settings/organization/customers/CustomerAddBlockPage";
import { IS_MSP } from "common-ui-components/components/utils/Config";
import CustomerSummaryForModule from "app/settings/organization/customers/customer-summary/CustomerSummaryForModule";

import Home from "app/home/Home";
import { isPublicRoute } from "utils";

const DownloadPopup = lazy(
  () => import("./app/enrollment-new/base/DownloadPopup"),
);

const Enrollment = lazy(() => import("app/enrollment-new/base/Enrollment"));
const InventoryPage = lazy(() => import("app/inventory-new/base/Inventory"));
const DeviceOverview = lazy(
  () => import("app/inventory-new/devices/device/Device"),
);
const Alerts = lazy(() => import("app/inventory-new/alerts/Alerts"));
const Devices = lazy(() => import("app/manage/base/Devices"));
const ReportsBase = lazy(() => import("app/reports/base/ReportsBase"));
const Tools = lazy(() => import("app/remote/base/Tools"));
const SettingsPage = lazy(() => import("app/settings/base/SettingsPage"));
const ApiDocumentation = lazy(
  () => import("app/api-documentation/ApiDocumentations"),
);
const Patch = lazy(() => import("./app/patch/Patch"));
const Console = lazy(() => import("./app/admin/console/Console"));

const withSuspense = (Component) => (
  <Suspense
    fallback={
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        >
        <ResponseLoader />
      </div>
    }
    >
    <Component />
  </Suspense>
);

const DeviceOverviewPage = () => (
  <DeviceOverview parentPath="/app/device-details" />
);

const getAppRoutes = () => [
  {
    type: "redirect",
    from: "/app",
    to: IS_MSP ? "/app/home-customer-summary" : "/app/home",
    key: "HOME",
    exact: true,
    products: getProductsByRouteGroup("HOME"),
  },
  {
    type: "redirect",
    from: "/app",
    to: "/app/tools",
    key: "REMOTE",
    exact: true,
    products: ["ra"],
  },
  {
    type: "redirect",
    from: "/app",
    to: "/app/secure",
    key: "PATCH",
    exact: true,
    products: ["vm"],
  },
    {
    type: "redirect",
    from: "/app",
    to: "/app/secure/patch-view/missing-patches",
    key: "PATCH",
    exact: true,
    products: ["pm"],
  },
  {
    type: "route",
    path: "/app/home-customer-summary",
    products: getProductsByRouteGroup("HOME"),
    key: "HOME_CUSTOMER_SUMMARY",
    render: () => (
      <Root sideNavBarCompat>
        <CustomerSummaryForModule
          moduleName={"home"}
          redirectUrl={"/app/home"}
        />
      </Root>
    ),
    renderOnlyIf: IS_MSP,
  },
  {
    type: "route",
    path: "/app/home",
    products: getProductsByRouteGroup("HOME"),
    key: "HOME",
    render: () => <Root sideNavBarCompat>{withSuspense(Home)}</Root>,
  },
  {
    type: "route",
    path: "/app/enrollment",
    products: getProductsByRouteGroup("ENROLLMENT"),
    key: "ENROLLMENT",
    render: () => <Root sideNavBarCompat>{withSuspense(Enrollment)}</Root>,
  },
  {
    type: "route",
    path: "/app/inventory-customer-summary",
    products: getProductsByRouteGroup("INVENTORY"),
    key: "INVENTORY_CUSTOMER_SUMMARY",
    render: () => (
      <Root sideNavBarCompat>
        <CustomerSummaryForModule
          moduleName={"inventory"}
          redirectUrl={"/app/inventory"}
        />
      </Root>
    ),
    renderOnlyIf: IS_MSP,
  },
  {
    type: "route",
    path: "/app/inventory",
    products: getProductsByRouteGroup("INVENTORY"),
    key: "INVENTORY",
    render: () => <Root sideNavBarCompat>{withSuspense(InventoryPage)}</Root>,
  },
  {
    type: "redirect",
    from: "/app/device-details",
    to: "/app/device-details/overview",
    exact: true,
  },
  {
    type: "route",
    path: "/app/device-details/*",
    render: () => (
      <Root sideNavBarCompat>
        {withSuspense(DeviceOverviewPage)}
      </Root>
    ),
  },
  {
    type: "route",
    path: "/app/alerts",
    products: getProductsByRouteGroup("ALERTS"),
    key: "ALERTS",
    render: () => <Root sideNavBarCompat>{withSuspense(Alerts)}</Root>,
  },
  {
    type: "route",
    path: "/app/manage",
    products: getProductsByRouteGroup("MANAGE"),
    key: "MANAGE",
    render: () => <Root sideNavBarCompat>{withSuspense(Devices)}</Root>,
  },
  {
    type: "route",
    path: "/app/reports",
    products: getProductsByRouteGroup("REPORTS"),
    key: "REPORTS",
    render: () => <Root sideNavBarCompat>{withSuspense(ReportsBase)}</Root>,
  },
  {
    type: "route",
    path: "/app/tools",
    products: getProductsByRouteGroup("REMOTE"),
    key: "REMOTE",
    render: () => <Root sideNavBarCompat>{withSuspense(Tools)}</Root>,
  },
  {
    type: "route",
    path: "/app/settings",
    products: getProductsByRouteGroup("SETTINGS"),
    key: "SETTINGS",
    render: () => <Root sideNavBarCompat>{withSuspense(SettingsPage)}</Root>,
  },
  {
    type: "route",
    path: "/app/api-explorer",
    key: "API_EXPLORER",
    render: () => (
      <Root hideSideBar={true} sideNavBarCompat>
        {withSuspense(ApiDocumentation)}
      </Root>
    ),
  },
  {
    type: "route",
    path: "/app/patches-customer-summary",
    products: getProductsByRouteGroup("PATCH"),
    key: "PATCH_CUSTOMER_SUMMARY",
    render: () => (
      <Root sideNavBarCompat>
        <CustomerSummaryForModule
          moduleName={"patch"}
          redirectUrl={"/app/secure"}
        />
      </Root>
    ),
    renderOnlyIf: IS_MSP,
  },
  {
    type: "route",
    path: "/app/secure",
    products: getProductsByRouteGroup("PATCH"),
    key: "PATCH",
    render: () => <Root sideNavBarCompat>{withSuspense(Patch)}</Root>,
  },
];

export const getCurrentlyRederedModule = () => {
  const rawPath = window.location.pathname;
  const resultPath = `/${rawPath.split("/").slice(2, 4).join("/")}`;
  if (resultPath.startsWith("/app/device-details")) {
    const product = rawPath.split("/")[1];
    const productModules = PRODUCT_CONFIG[product]?.routeGroups || [];
    return (
      ["INVENTORY", "REMOTE", "PATCH"].find((module) =>
        productModules.includes(module),
      ) || "UNKNOWN"
    );
  }
  const appRoutes = getAppRoutes();
  const match = appRoutes.find(
    (route) => route.path && resultPath.startsWith(route.path),
  );
  return match?.key || "UNKNOWN";
};

const isRouteAllowed = (route, modes) =>
  !route.products || route.products.some((p) => modes.includes(p));

export const UserDetailsContext = createContext({});
const routes = { BASE_PATH: "/app" };

function App({ userDetails, setUserDetails, appModes }) {
  const [openBanner, setOpenBanner] = useState(true);

  const idleRef = useRef(null);
  const stompClientRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_WMS_RECONNECTS = 5;

  const [isNewCustomerPage, setIsNewCustomerPage] = useState(
    window.location.pathname.includes("/new-customer"),
  );

  useEffect(() => {
    const checkPath = () => {
      setIsNewCustomerPage(window.location.pathname.includes("/new-customer"));
    };

    window.addEventListener("popstate", checkPath);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      checkPath();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      checkPath();
    };

    return () => {
      window.removeEventListener("popstate", checkPath);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  /* ---------- IDLE LOGOUT ---------- */
  useEffect(() => {
    idleRef.current = new IdleJs({
      idle: (userDetails.sessionIdleTimeout || 1) * 60 * 60 * 1000,
      onIdle: logOut,
      events: ["mousemove", "keydown", "mousedown", "touchstart", "scroll"],
    });
    idleRef.current.start();
    return () => idleRef.current.stop();
  }, [userDetails.sessionIdleTimeout]);

  //charge bee api call
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.chargebee.com/v2/chargebee.js";
    script.dataset.cbSite = process.env.REACT_APP_PAYMENT_SITE;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  /* ---------- WEBSOCKET ---------- */
  useEffect(() => {
    if (!userDetails?.orgId) return;

    if (!stompClientRef.current) {
      stompClientRef.current = new Client({
        webSocketFactory: () => new SockJS(process.env.REACT_APP_WMS_URL),
        reconnectDelay: 0,
      });
    }

    const client = stompClientRef.current;
    if (client.active) return;

    client.onConnect = () => {
      retryCountRef.current = 0;

      client.subscribe("/user/updates", (m) => handleWmsMessage(m.body));

      client.subscribe(`/org/${userDetails.orgId}`, (m) =>
        handleWmsMessage(m.body),
      );
    };

    client.onWebSocketClose = () => {
      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_WMS_RECONNECTS) {
        console.error("WMS stopped after 5 reconnect attempts");
        client.deactivate();
      }
    };

    client.activate();

    return () => {
      if (client.active) client.deactivate();
    };
  }, [userDetails?.orgId]);

  window.localStorage.setItem("UserName", userDetails?.name || "name");
  window.localStorage.setItem("UserRole", userDetails?.roleName || "role");
  window.localStorage.setItem("Plan", userDetails?.plan || "plan");
  window.localStorage.setItem(
    "SuperAdminEmail",
    userDetails?.ownerEmail || "email",
  );
  window.localStorage.setItem(
    "IsAccountVerified",
    userDetails?.verified ? "true" : "false",
  );

  if (!appModes) return <ResponseLoader fullScreen text="Loading" />;

  const appRoutes = getAppRoutes();

  const basePath =
    appModes.length === 1
      ? `/${appModes[0]}`
      : appModes.includes("em")
        ? "/em"
        : `/${appModes[0]}`;

  const showEmailVerificationBanner =
    userDetails &&
    userDetails.hasOwnProperty("verified") &&
    userDetails.verified === false;

  const showSimulationDataBanner =
    userDetails &&
    userDetails.deviceSimulationData === true &&
    !userDetails.expired &&
    !isPublicRoute(window.location.pathname) &&
    !window.location.pathname.includes("admin-console");

  return (
    <div className={style.app}>
      <UserDetailsContext.Provider value={{ ...userDetails, setUserDetails }}>
        {(showEmailVerificationBanner || showSimulationDataBanner) && (
          <TopBanner
            type="emailVerification"
            showSimulationDataBanner={showSimulationDataBanner}
          />
        )}
        {userDetails &&
          userDetails.hasOwnProperty("bannerMessage") &&
          userDetails.bannerMessage &&
          openBanner && (
            <TopBanner
              msg={userDetails.bannerMessage}
              setOpenBanner={setOpenBanner}
            />
          )}

        <BrowserRouter basename={basePath}>
          <DynamicPageTitle basePath={basePath} />
          <RestrictionChecker />
          <Notification />
          <FeatureHider />
          <Routes>
            <Route path="/" element={<CustomPublicRoute />} />

            <Route
              path="/try-product"
              element={
                <Root sideNavBarCompat id="main" hideSideBar>
                  <TryProduct />
                </Root>
              }
            />

            <Route
              path="/agent-download/:key"
              element={
                <Suspense fallback={<ResponseLoader />}>
                  <DownloadPopup />
                </Suspense>
              }
            />

            <Route
              path={`${routes.BASE_PATH}/*`}
              element={
                <CustomPrivateRoute>
                  <Base />
                </CustomPrivateRoute>
              }
            />

            <Route
              path="/admin-console/*"
              element={
                <Suspense fallback={<ResponseLoader />}>
                  <Console />
                </Suspense>
              }
            />

            <Route path="*" element={<Error404 />} />
          </Routes>
        </BrowserRouter>
      </UserDetailsContext.Provider>
    </div>
  );

  function Base() {
    useNotificationAutoCleanup();

    const permittedRoutes = appRoutes.filter((r) =>
      isRouteAllowed(r, appModes),
    );

    // checking for one customer (already added all customer) in MSP without customer to show the block page for adding customer
    const shouldBlock =
      IS_MSP && userDetails?.customers?.length === 1 && !isNewCustomerPage;

    return (
      <Contexts>
        <Routes>
          {renderPermittedRoutes(permittedRoutes, "Root")}
          <Route path="*" element={<Error404 />} />
        </Routes>

        {shouldBlock && (
          <ModalV2
            open={shouldBlock}
            setOpen={() => {}}
            closeOnOutsideClick={false}
          >
            <CustomerAddBlockPage />
          </ModalV2>
        )}
      </Contexts>
    );
  }
}

/* ---------- ROUTES ---------- */
export function CustomPublicRoute() {
  return <PublicRoute redirectUrl="/" baseUrl={routes.BASE_PATH} />;
}

export function CustomPrivateRoute({ children }) {
  return <PrivateRoute redirectUrl="/">{children}</PrivateRoute>;
}

export default App;